export type SelectableRegionFeature = {
  id?: string | number;
  properties?: Record<string, unknown> | null;
};

const layerCleanups = new WeakMap<
  SelectableRegionMap,
  Map<string, () => void>
>();

export type SelectableRegionMap = {
  addSource: (
    id: string,
    source: {
      type: "geojson";
      data: unknown;
      promoteId?: string;
    },
  ) => void;
  addLayer: (layer: Record<string, unknown>) => void;
  removeLayer: (id: string) => void;
  removeSource: (id: string) => void;
  setFilter: (layerId: string, filter: unknown) => void;
  setFeatureState: (
    target: { source: string; id: string | number },
    state: Record<string, unknown>,
  ) => void;
  on: (
    event: string,
    layerId: string,
    handler: (event: {
      features?: SelectableRegionFeature[];
    }) => void,
  ) => void;
  off: (
    event: string,
    layerId: string,
    handler: (event: {
      features?: SelectableRegionFeature[];
    }) => void,
  ) => void;
};

export function addSelectableRegionLayer(
  map: SelectableRegionMap,
  {
    sourceId,
    data,
    country,
    onSelect,
  }: {
    sourceId: string;
    data: unknown;
    country: string;
    onSelect: (feature: SelectableRegionFeature) => void;
  },
): () => void {
  const fillLayerId = `${sourceId}-fill`;
  const lineLayerId = `${sourceId}-line`;
  let hoveredId: string | number | undefined;
  const hoverHandler = (event: { features?: SelectableRegionFeature[] }) => {
    const feature = event.features?.[0];
    if (hoveredId !== undefined && hoveredId !== feature?.id) {
      map.setFeatureState(
        { source: sourceId, id: hoveredId },
        { hover: false },
      );
    }
    if (feature?.id !== undefined) {
      hoveredId = feature.id;
      map.setFeatureState(
        { source: sourceId, id: feature.id },
        { hover: true },
      );
    }
  };
  const leaveHandler = () => {
    if (hoveredId !== undefined) {
      map.setFeatureState(
        { source: sourceId, id: hoveredId },
        { hover: false },
      );
      hoveredId = undefined;
    }
  };
  const clickHandler = (event: { features?: SelectableRegionFeature[] }) => {
    const feature = event.features?.[0];
    if (feature) {
      onSelect(feature);
    }
  };

  layerCleanups.get(map)?.get(sourceId)?.();

  map.addSource(sourceId, {
    type: "geojson",
    // Keep the GeoJSON feature id. promoteId: "id" reads a missing
    // properties.id and clears it, so the clicked county never highlights.
    data,
  });
  map.addLayer({
    id: fillLayerId,
    type: "fill",
    source: sourceId,
    filter: ["==", ["get", "country"], country],
    paint: {
      "fill-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        "#059669",
        ["boolean", ["feature-state", "hover"], false],
        "#34d399",
        "#d1fae5",
      ],
      "fill-opacity": 0.6,
    },
  });
  map.addLayer({
    id: lineLayerId,
    type: "line",
    source: sourceId,
    filter: ["==", ["get", "country"], country],
    paint: {
      "line-color": "#047857",
      "line-width": 1,
    },
  });
  map.on("mousemove", fillLayerId, hoverHandler);
  map.on("mouseleave", fillLayerId, leaveHandler);
  map.on("click", fillLayerId, clickHandler);

  const remove = () => {
    map.off("mousemove", fillLayerId, hoverHandler);
    map.off("mouseleave", fillLayerId, leaveHandler);
    map.off("click", fillLayerId, clickHandler);
    map.removeLayer(lineLayerId);
    map.removeLayer(fillLayerId);
    map.removeSource(sourceId);
    const registered = layerCleanups.get(map);
    if (registered?.get(sourceId) === remove) {
      registered.delete(sourceId);
    }
  };
  const bySource = layerCleanups.get(map) ?? new Map<string, () => void>();
  bySource.set(sourceId, remove);
  layerCleanups.set(map, bySource);
  return remove;
}
